import { useState, useRef, useEffect, useCallback, useMemo, type JSX, type MouseEvent, type KeyboardEvent } from "react"
import { DockviewDefaultTab, type IDockviewPanelHeaderProps } from "dockview"
import { IconButton, Menu, MenuItem } from "@mui/material"
import { MoreVert } from "@mui/icons-material"
import OPTooltip from "components/material/tooltip"
import { DOCKVIEW_LOCATION_TYPE_FLOATING, type OPLayoutPanelTabOptions } from "./types"
import "./custom-tab.scss"

interface CustomTabComponentProps extends IDockviewPanelHeaderProps, OPLayoutPanelTabOptions {}

function useTabTitle(api: CustomTabComponentProps["api"]) {
  const [title, setTitle] = useState(api.title)

  useEffect(() => {
    const disposable = api.onDidTitleChange((event) => {
      setTitle(event.title)
    })
    return () => disposable.dispose()
  }, [api])

  return title
}

export default function CustomTabComponent(props: CustomTabComponentProps): JSX.Element {
  const {
    removable,
    removePanel,
    removeRequiresConfirmation = false,
    editable,
    onUpdateTitle,
    duplicatePanel,
    icon: Icon,
    api,
    ...dockviewTabProps
  } = props
  const tabTitle = useTabTitle(api)
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(api.title)
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null)
  const [locationType, setLocationType] = useState(() => api.location.type)
  const inputRef = useRef<HTMLInputElement>(null)

  const showMenu = editable || removable || typeof duplicatePanel === "function"

  useEffect(() => {
    const disposable = api.onDidLocationChange(() => {
      setLocationType(api.location.type)
    })
    return () => disposable.dispose()
  }, [api])

  const onContextMenu = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
  }, [])

  const handleMenuOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
  }, [])

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null)
  }, [])

  const handleDelete = useCallback((event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation()
    handleMenuClose()
    if (removePanel) {
      removePanel(api.id, removeRequiresConfirmation)
    }
  }, [removePanel, api.id, handleMenuClose, removeRequiresConfirmation])

  const handleDuplicate = useCallback((event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation()
    handleMenuClose()
    duplicatePanel?.()
  }, [duplicatePanel, handleMenuClose])

  const handleRename = useCallback((event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation()
    handleMenuClose()
    setEditValue(tabTitle)
    setIsEditing(true)
  }, [tabTitle, handleMenuClose])

  const handleDoubleClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    if (editable && onUpdateTitle) {
      event.stopPropagation()
      setEditValue(tabTitle)
      setIsEditing(true)
    }
  }, [editable, onUpdateTitle, tabTitle])

  const handleBlur = useCallback(() => {
    if (isEditing) {
      const trimmedValue = editValue?.trim() ?? ""
      if (trimmedValue && trimmedValue !== tabTitle && onUpdateTitle) {
        onUpdateTitle(api.id, trimmedValue)
      }
      setIsEditing(false)
    }
  }, [isEditing, editValue, tabTitle, onUpdateTitle, api.id])

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault()
      inputRef.current?.blur()
    } else if (event.key === "Escape") {
      event.preventDefault()
      setEditValue(tabTitle)
      setIsEditing(false)
    }
  }, [tabTitle])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Derive edit value from api.title when not editing
  // This ensures the value stays in sync without using setState in an effect
  const displayValue = isEditing ? editValue : tabTitle

  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  const handleTooltipOpen = useCallback(() => {
    setIsTooltipOpen(true)
  }, [])

  const handleTooltipClose = useCallback(() => {
    setIsTooltipOpen(false)
  }, [])

  const isFloatingPanel = locationType === DOCKVIEW_LOCATION_TYPE_FLOATING
  const shiftDragHint = isFloatingPanel
    ? "Shift+drag to move as floating window"
    : "Shift+drag to open in a floating window"

  const tooltipTitle = useMemo(
    () => (
      <span>
        <span className="custom-tab-tooltip-title">{tabTitle}</span>
        <span className="custom-tab-tooltip-hint">{shiftDragHint}</span>
      </span>
    ),
    [tabTitle, shiftDragHint],
  )

  if (isEditing) {
    return (
      <div className="custom-tab-wrapper custom-tab-editing" data-panel-id={api.id}>
        <input
          ref={inputRef}
          type="text"
          className="custom-tab-input"
          value={displayValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )
  }

  return (
    <div className="custom-tab-wrapper" onDoubleClick={handleDoubleClick} data-panel-id={api.id}>
      <OPTooltip
        key={`${tabTitle ?? ""}|${shiftDragHint}`}
        title={tooltipTitle}
        open={isTooltipOpen}
        onOpen={handleTooltipOpen}
        onClose={handleTooltipClose}
      >
        <span className="custom-tab-tooltip-target" onClick={handleTooltipClose}>
          {Icon && (
            <span className="custom-tab-icon">
              <Icon fontSize="small" />
            </span>
          )}
          <DockviewDefaultTab
            onContextMenu={onContextMenu}
            api={api}
            {...dockviewTabProps}
            hideClose={true}
          />
        </span>
      </OPTooltip>
      {showMenu && (
        <>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            className="custom-tab-menu-button"
          >
            <MoreVert fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem onClick={handleRename} disabled={!editable}>Rename</MenuItem>
            <MenuItem onClick={handleDuplicate} disabled={!duplicatePanel}>Duplicate</MenuItem>
            <MenuItem onClick={handleDelete} disabled={!removable}>{removeRequiresConfirmation ? "Delete" : "Remove"}</MenuItem>
          </Menu>
        </>
      )}
    </div>
  )
}
